import { useState, useRef, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { API, formatApiError } from "../lib/api";
import { useSettings } from "../contexts/SettingsContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Upload, CheckCircle2, Lock } from "lucide-react";
import { toast } from "sonner";
import { ArabicLevelBlock } from "../components/ArabicLevelFields";

const MATIERES = [
  { key: "arabe", label: "Arabe" },
  { key: "coran", label: "Coran" },
  { key: "education_musulmane", label: "Éducation Musulmane" },
];

const initial = {
  rl_prenom: "", rl_nom: "", rl_tel_fixe: "", rl_tel_portable: "", rl_email: "",
  rl_participe_activites_mosquee: null, rl_activites_mosquee: "",
  rl_prieres_regulieres_mosquee: null,
  enf_prenom: "", enf_nom: "", enf_adresse: "", enf_ville: "",
  enf_date_naissance: "", enf_age: "", enf_autorisation_rentrer_seul: null,
  a_des_freres_soeurs: null, freres_soeurs_details: "",
  premiere_inscription: true, cours_arabe_suivi: null, cours_arabe_anterieur: "",
  photo_path: "",
  niveau_arabe_ecrit: 0, niveau_arabe_oral: 0, nb_sourates: 0, sourates_connues: [],
  traitement_medical: null, traitement_medical_detail: "",
  allergies: null, allergies_detail: "",
  allergies_alimentaires: null, allergies_alimentaires_detail: "",
  maladies_chroniques: null, maladies_chroniques_detail: "",
  autres_renseignements: "",
  urg_prenom: "", urg_nom: "", urg_tel_fixe: "", urg_tel_portable: "",
  autorisation_photos_classe: null,
  matieres_suivies: ["arabe", "coran", "education_musulmane"],
};

function computeAge(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return "";
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
  return age;
}

export default function EnrollmentRequestPage() {
  const navigate = useNavigate();
  const { enrollmentOpen, loading: settingsLoading, feeTiers, activeYear } = useSettings();
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleMat = (k) => set("matieres_suivies", form.matieres_suivies.includes(k)
    ? form.matieres_suivies.filter((x) => x !== k)
    : [...form.matieres_suivies, k]);

  // Calcul de la limite d'âge : enfant doit avoir 6 ans au 30/09 de l'année active
  const ageCheck = useMemo(() => {
    if (!activeYear?.label || !form.enf_date_naissance) return { ok: true, cutoff: null, ageAtCutoff: null };
    const startYear = parseInt(activeYear.label.split("/")[0], 10);
    if (!startYear) return { ok: true, cutoff: null, ageAtCutoff: null };
    const cutoff = new Date(startYear, 8, 30); // 30 septembre (mois 8 = septembre)
    const dob = new Date(form.enf_date_naissance);
    if (Number.isNaN(dob.getTime())) return { ok: true, cutoff, ageAtCutoff: null };
    // Âge en années à la date cutoff
    let age = cutoff.getFullYear() - dob.getFullYear();
    const m = cutoff.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && cutoff.getDate() < dob.getDate())) age--;
    const cutoffFr = `${String(cutoff.getDate()).padStart(2, "0")}/${String(cutoff.getMonth() + 1).padStart(2, "0")}/${cutoff.getFullYear()}`;
    return { ok: age >= 6, cutoff: cutoffFr, ageAtCutoff: age, startYear };
  }, [activeYear, form.enf_date_naissance]);

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await fetch(`${API}/upload`, { method: "POST", body: fd });
      if (!r.ok) throw new Error((await r.json()).detail || "Upload failed");
      const data = await r.json();
      set("photo_path", data.path);
      toast.success("Photo téléchargée");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!ageCheck.ok) {
      toast.error(`L'enfant doit avoir 6 ans révolus au plus tard le ${ageCheck.cutoff}. Inscription impossible cette année.`);
      return;
    }
    if (form.matieres_suivies.length === 0) {
      toast.error("Sélectionnez au moins une matière souhaitée");
      return;
    }
    if (!form.photo_path) {
      toast.error("Veuillez téléverser une photo de l'enfant");
      return;
    }
    // Toutes les questions Oui/Non sont obligatoires
    const requiredYesNo = [
      ["rl_participe_activites_mosquee", "Activités à la Mosquée"],
      ["rl_prieres_regulieres_mosquee", "Prières régulières à la Mosquée"],
      ["enf_autorisation_rentrer_seul", "Rentrer seul après les cours"],
      ["a_des_freres_soeurs", "Frères/sœurs à la Mosquée"],
      ["traitement_medical", "Traitement médical"],
      ["allergies", "Allergies"],
      ["allergies_alimentaires", "Allergies alimentaires"],
      ["maladies_chroniques", "Maladies chroniques"],
      ["autorisation_photos_classe", "Autorisation photos"],
    ];
    for (const [k, lbl] of requiredYesNo) {
      if (form[k] === null || form[k] === undefined) {
        toast.error(`Répondez à : ${lbl}`);
        return;
      }
    }
    // Détails obligatoires si "oui"
    const requiredIfYes = [
      ["rl_participe_activites_mosquee", "rl_activites_mosquee", "Précisez les activités"],
      ["a_des_freres_soeurs", "freres_soeurs_details", "Précisez prénoms/noms des frères/sœurs"],
      ["traitement_medical", "traitement_medical_detail", "Précisez le traitement"],
      ["allergies", "allergies_detail", "Précisez les allergies"],
      ["allergies_alimentaires", "allergies_alimentaires_detail", "Précisez les allergies alimentaires"],
      ["maladies_chroniques", "maladies_chroniques_detail", "Précisez les maladies chroniques"],
    ];
    for (const [yk, dk, lbl] of requiredIfYes) {
      if (form[yk] === true && !(form[dk] || "").trim()) {
        toast.error(lbl);
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        enf_age: form.enf_age ? parseInt(form.enf_age) : computeAge(form.enf_date_naissance) || null,
        nombre_matieres: form.matieres_suivies.length,
        rl_tel_fixe: form.rl_tel_fixe || null,
        urg_tel_fixe: form.urg_tel_fixe || null,
        photo_path: form.photo_path || null,
        cours_arabe_anterieur: form.cours_arabe_anterieur || null,
        groupe_souhaite: null,
        freres_soeurs_details: form.freres_soeurs_details || null,
        traitement_medical_detail: form.traitement_medical_detail || null,
        allergies_detail: form.allergies_detail || null,
        allergies_alimentaires_detail: form.allergies_alimentaires_detail || null,
        maladies_chroniques_detail: form.maladies_chroniques_detail || null,
        autres_renseignements: form.autres_renseignements || null,
        rl_activites_mosquee: form.rl_activites_mosquee || null,
      };
      await api.post("/enrollment-requests", payload);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center fade-in" data-testid="request-success">
        <CheckCircle2 className="w-16 h-16 mx-auto text-[#1E7A4D]" strokeWidth={1.5} />
        <h1 className="mt-5 font-[Outfit] font-semibold text-3xl text-[#1A2547]">Demande envoyée</h1>
        <p className="mt-3 text-[#5C6861] leading-relaxed">
          Nous avons bien reçu votre demande d'inscription. Notre équipe l'étudiera dans les meilleurs délais et vous contactera par email pour la suite. En fonction des places disponibles, nous vous indiquerons si votre demande d'inscription est acceptée ou non. Si elle est acceptée, nous vous communiquerons la date de la permanence pour le règlement de la cotisation (payable en plusieurs fois).
        </p>
        <Button onClick={() => navigate("/")} className="mt-8 rounded-full bg-[#1E3A8A] hover:bg-[#14265E]">Retour à l'accueil</Button>
      </div>
    );
  }

  if (!settingsLoading && !enrollmentOpen) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center fade-in" data-testid="enrollment-closed-page">
        <div className="w-16 h-16 rounded-full bg-[#E2E5EE] flex items-center justify-center mx-auto">
          <Lock className="w-7 h-7 text-[#5c6374]" strokeWidth={1.5} />
        </div>
        <h1 className="mt-5 font-[Outfit] font-semibold text-3xl text-[#1A2547]">Les inscriptions ne sont pas ouvertes</h1>
        <p className="mt-3 text-[#5C6861] leading-relaxed">
          Les demandes d'inscription sont actuellement fermées. Merci de revenir ultérieurement ou de contacter directement la mosquée pour plus d'informations.
        </p>
        <Link to="/" className="inline-block mt-8">
          <Button className="rounded-full bg-[#1E3A8A] hover:bg-[#14265E]">Retour à l'accueil</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-12 fade-in" data-testid="request-form-page">
      <span className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C86444]">Inscription</span>
      <h1 className="mt-3 font-[Outfit] font-semibold text-3xl md:text-4xl tracking-tight text-[#1A2547]">Formulaire de demande d'inscription</h1>
      <p className="text-[#5C6861] mt-3">Veuillez remplir l'ensemble des champs. Cotisation annuelle par famille : <strong>{Math.round(Number(feeTiers.one_child))} €</strong> (1 enfant) · <strong>{Math.round(Number(feeTiers.two_children))} €</strong> (2 enfants) · <strong>{Math.round(Number(feeTiers.three_plus_children))} €</strong> (3+ enfants). À régler en présentiel après validation.</p>

      <form onSubmit={submit} className="mt-10 space-y-8" data-testid="request-form">
        {/* Représentant légal */}
        <Section title="Représentant légal">
          <Grid2>
            <Field label="Prénom *"><Input required value={form.rl_prenom} onChange={(e) => set("rl_prenom", e.target.value)} className="rounded-xl" data-testid="rl_prenom" /></Field>
            <Field label="Nom *"><Input required value={form.rl_nom} onChange={(e) => set("rl_nom", e.target.value)} className="rounded-xl" data-testid="rl_nom" /></Field>
            <Field label="Téléphone fixe"><Input value={form.rl_tel_fixe} onChange={(e) => set("rl_tel_fixe", e.target.value)} className="rounded-xl" /></Field>
            <Field label="Téléphone portable *"><Input required value={form.rl_tel_portable} onChange={(e) => set("rl_tel_portable", e.target.value)} className="rounded-xl" data-testid="rl_tel_portable" /></Field>
            <FieldFull label="Email *"><Input required type="email" value={form.rl_email} onChange={(e) => set("rl_email", e.target.value)} className="rounded-xl" data-testid="rl_email" /></FieldFull>
          </Grid2>
          <YesNoRadio id="rl_part" label="Participez-vous à l'une des activités de la Mosquée de Teisseire ?" value={form.rl_participe_activites_mosquee} onChange={(v) => set("rl_participe_activites_mosquee", v)} />
          {form.rl_participe_activites_mosquee === true && (
            <Field label="À quelle(s) activité(s) participez-vous ? *"><Textarea required value={form.rl_activites_mosquee} onChange={(e) => set("rl_activites_mosquee", e.target.value)} className="rounded-xl" /></Field>
          )}
          <YesNoRadio id="rl_prieres" label="Pratiquez-vous régulièrement vos prières à la Mosquée de Teisseire ?" value={form.rl_prieres_regulieres_mosquee} onChange={(v) => set("rl_prieres_regulieres_mosquee", v)} />
        </Section>

        {/* Enfant */}
        <Section title="Enfant">
          <Grid2>
            <Field label="Prénom *"><Input required value={form.enf_prenom} onChange={(e) => set("enf_prenom", e.target.value)} className="rounded-xl" data-testid="enf_prenom" /></Field>
            <Field label="Nom *"><Input required value={form.enf_nom} onChange={(e) => set("enf_nom", e.target.value)} className="rounded-xl" data-testid="enf_nom" /></Field>
            <FieldFull label="Adresse *"><Input required value={form.enf_adresse} onChange={(e) => set("enf_adresse", e.target.value)} className="rounded-xl" /></FieldFull>
            <Field label="Ville *"><Input required value={form.enf_ville} onChange={(e) => set("enf_ville", e.target.value)} className="rounded-xl" /></Field>
            <Field label="Date de naissance *">
              <Input required type="date" value={form.enf_date_naissance}
                onChange={(e) => { set("enf_date_naissance", e.target.value); set("enf_age", computeAge(e.target.value)); }}
                className="rounded-xl" data-testid="enf_date_naissance" />
            </Field>
            <Field label="Âge"><Input type="number" readOnly disabled value={form.enf_age || ""} className="rounded-xl bg-[#FCFBF8]" /></Field>
          </Grid2>
          <YesNoRadio id="autorise_seul" label="L'enfant est-il autorisé à rentrer seul à son domicile après les cours ?" value={form.enf_autorisation_rentrer_seul} onChange={(v) => set("enf_autorisation_rentrer_seul", v)} />
          <YesNoRadio id="fratrie" label="Cet enfant a-t-il un frère ou une sœur déjà inscrit(e) ou en cours d'inscription à la Mosquée des Alpes ?" value={form.a_des_freres_soeurs} onChange={(v) => set("a_des_freres_soeurs", v)} />
          {form.a_des_freres_soeurs === true && (
            <Field label="Précisez le ou les prénoms et noms du/des frère(s) et sœur(s) (cela nous aidera à organiser les cours sur le même jour si possible) *">
              <Textarea required value={form.freres_soeurs_details} onChange={(e) => set("freres_soeurs_details", e.target.value)} className="rounded-xl" data-testid="fratrie_details" placeholder="Ex: Yusuf Benali (12 ans), Aïcha Benali (8 ans)" />
            </Field>
          )}
        </Section>

        {/* Bandeau bloquant si l'enfant n'aura pas 6 ans au 30/09 de l'année active */}
        {!ageCheck.ok && form.enf_date_naissance && ageCheck.cutoff && (
          <div className="bg-[#FBE5E4] border-2 border-[#B0413E] rounded-2xl p-5" data-testid="age-block-banner">
            <div className="flex items-start gap-3">
              <Lock className="w-6 h-6 text-[#B0413E] shrink-0 mt-0.5" />
              <div>
                <h3 className="font-[Outfit] font-semibold text-base text-[#B0413E]">Âge minimum non atteint — Inscription impossible cette année</h3>
                <p className="text-sm text-[#1A2547] mt-2 leading-relaxed">
                  Pour s'inscrire à l'année <strong>{activeYear?.label}</strong>, l'enfant doit avoir
                  <strong> 6 ans révolus au plus tard le {ageCheck.cutoff}</strong>.
                </p>
                <p className="text-sm text-[#5c6374] mt-1">
                  Avec la date de naissance renseignée, votre enfant aura <strong>{ageCheck.ageAtCutoff} an{Math.abs(ageCheck.ageAtCutoff) > 1 ? "s" : ""}</strong> à cette date.
                </p>
                <p className="text-xs text-[#5c6374] mt-3 italic">
                  Nous vous invitons à représenter lorsque votre enfant aura 6 ans au plus tard le 30/09 de l'année en cours. Pour toute question, n'hésitez pas à nous contacter.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Le reste du formulaire est désactivé si l'enfant n'a pas l'âge requis */}
        <fieldset disabled={!ageCheck.ok} className={!ageCheck.ok ? "opacity-50 pointer-events-none space-y-8" : "space-y-8"}>

        {/* Inscription */}
        <Section title="Historique d'inscription">
          <div>
            <Label className="text-sm">S'agit-il d'une première inscription ?</Label>
            <RadioGroup value={form.premiere_inscription ? "oui" : "non"} onValueChange={(v) => set("premiere_inscription", v === "oui")} className="mt-2 flex gap-6">
              <div className="flex items-center gap-2"><RadioGroupItem value="oui" id="pi_oui" /><Label htmlFor="pi_oui">Oui</Label></div>
              <div className="flex items-center gap-2"><RadioGroupItem value="non" id="pi_non" /><Label htmlFor="pi_non">Non</Label></div>
            </RadioGroup>
          </div>
          <div>
            <Label className="text-sm">Photo de l'enfant *</Label>
            <div className="mt-2 flex items-center gap-3">
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handlePhoto} className="hidden" data-testid="photo-input" />
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-full border-[#1E3A8A] text-[#1E3A8A]">
                <Upload className="w-4 h-4 mr-1.5" /> {uploading ? "Téléchargement…" : form.photo_path ? "Remplacer la photo" : "Téléverser une photo"}
              </Button>
              {form.photo_path
                ? <span className="text-xs text-[#1E7A4D]">✓ Téléchargée</span>
                : <span className="text-xs text-[#C86444]">Obligatoire</span>
              }
            </div>
          </div>
        </Section>

        {/* Niveau d'arabe & sourates */}
        <Section title="Niveau d'arabe & Coran">
          <p className="text-xs text-[#5c6374] -mt-2">Aidez-nous à orienter l'enfant dans le bon groupe. Si vous n'êtes pas sûr, laissez sur « Aucun » / 0.</p>
          <ArabicLevelBlock form={form} set={set} testIdPrefix="inscription" />
        </Section>

        {/* Médical */}
        <Section title="Informations médicales">
          <YesNoRadioDetail id="trait" label="L'enfant suit-il un traitement médical ?" value={form.traitement_medical} onChange={(v) => set("traitement_medical", v)} detail={form.traitement_medical_detail} setDetail={(v) => set("traitement_medical_detail", v)} placeholder="Précisez lequel" />
          <YesNoRadioDetail id="all" label="L'enfant a-t-il des allergies ?" value={form.allergies} onChange={(v) => set("allergies", v)} detail={form.allergies_detail} setDetail={(v) => set("allergies_detail", v)} placeholder="Précisez lesquelles" />
          <YesNoRadioDetail id="allal" label="L'enfant a-t-il des allergies alimentaires ?" value={form.allergies_alimentaires} onChange={(v) => set("allergies_alimentaires", v)} detail={form.allergies_alimentaires_detail} setDetail={(v) => set("allergies_alimentaires_detail", v)} placeholder="Précisez lesquelles" />
          <YesNoRadioDetail id="mc" label="L'enfant a-t-il des maladies chroniques ?" value={form.maladies_chroniques} onChange={(v) => set("maladies_chroniques", v)} detail={form.maladies_chroniques_detail} setDetail={(v) => set("maladies_chroniques_detail", v)} placeholder="Précisez lesquelles" />
          <Field label="Autres renseignements que vous jugez utiles (optionnel)">
            <Textarea value={form.autres_renseignements} onChange={(e) => set("autres_renseignements", e.target.value)} className="rounded-xl" />
          </Field>
        </Section>

        {/* Contact urgence */}
        <Section title="Contact en cas d'urgence">
          <Grid2>
            <Field label="Prénom *"><Input required value={form.urg_prenom} onChange={(e) => set("urg_prenom", e.target.value)} className="rounded-xl" /></Field>
            <Field label="Nom *"><Input required value={form.urg_nom} onChange={(e) => set("urg_nom", e.target.value)} className="rounded-xl" /></Field>
            <Field label="Téléphone fixe"><Input value={form.urg_tel_fixe} onChange={(e) => set("urg_tel_fixe", e.target.value)} className="rounded-xl" /></Field>
            <Field label="Téléphone portable *"><Input required value={form.urg_tel_portable} onChange={(e) => set("urg_tel_portable", e.target.value)} className="rounded-xl" /></Field>
          </Grid2>
        </Section>

        {/* Autorisations & matières */}
        <Section title="Autorisations & matières">
          <YesNoRadio id="auto_photo" label="J'autorise les photos en classe (utilisation interne et associative)." value={form.autorisation_photos_classe} onChange={(v) => set("autorisation_photos_classe", v)} />
          <div>
            <Label className="text-sm">Matières souhaitées * (la répartition finale sera décidée par l'équipe pédagogique)</Label>
            <div className="mt-3 grid sm:grid-cols-3 gap-2">
              {MATIERES.map((m) => (
                <label key={m.key} className={`flex items-center gap-2 px-4 py-3 rounded-xl border cursor-pointer transition-colors ${form.matieres_suivies.includes(m.key) ? "bg-[#E0E7FF] border-[#1E3A8A]" : "bg-white border-[#EAE6DF] hover:border-[#1E3A8A]"}`}>
                  <Checkbox checked={form.matieres_suivies.includes(m.key)} onCheckedChange={() => toggleMat(m.key)} data-testid={`mat-${m.key}`} />
                  <span className="text-sm font-medium">{m.label}</span>
                </label>
              ))}
            </div>
            <div className="text-xs text-[#8a968f] mt-2">Nombre de matières souhaitées : {form.matieres_suivies.length}</div>
          </div>
        </Section>

        </fieldset>

        <Button type="submit" disabled={submitting || !ageCheck.ok} className="w-full rounded-full bg-[#1E3A8A] hover:bg-[#14265E] text-white h-12 text-base disabled:opacity-50 disabled:cursor-not-allowed" data-testid="request-submit">
          {submitting ? "Envoi en cours…" : (!ageCheck.ok ? "Envoi indisponible (âge minimum non atteint)" : "Envoyer la demande")}
        </Button>
      </form>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-[#EAE6DF] rounded-2xl p-6 md:p-8 space-y-5">
      <h2 className="font-[Outfit] font-semibold text-xl text-[#1A2547] border-b border-[#EAE6DF] pb-3">{title}</h2>
      {children}
    </div>
  );
}
function Field({ label, children }) {
  return <div><Label className="text-sm">{label}</Label><div className="mt-1.5">{children}</div></div>;
}
function FieldFull({ label, children }) {
  return <div className="sm:col-span-2"><Label className="text-sm">{label}</Label><div className="mt-1.5">{children}</div></div>;
}
function Grid2({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>;
}
function CheckRow({ id, label, checked, onChange }) {
  return (
    <div className="flex items-start gap-3">
      <Checkbox id={id} checked={checked} onCheckedChange={onChange} className="mt-0.5" />
      <Label htmlFor={id} className="text-sm leading-snug cursor-pointer">{label}</Label>
    </div>
  );
}
function YesNoRadio({ id, label, value, onChange }) {
  const v = value === true ? "oui" : value === false ? "non" : "";
  return (
    <div>
      <Label className="text-sm leading-snug">{label} <span className="text-[#C86444]">*</span></Label>
      <RadioGroup value={v} onValueChange={(val) => onChange(val === "oui")} className="mt-2 flex gap-6" data-testid={id}>
        <div className="flex items-center gap-2"><RadioGroupItem value="oui" id={`${id}-oui`} /><Label htmlFor={`${id}-oui`} className="cursor-pointer">Oui</Label></div>
        <div className="flex items-center gap-2"><RadioGroupItem value="non" id={`${id}-non`} /><Label htmlFor={`${id}-non`} className="cursor-pointer">Non</Label></div>
      </RadioGroup>
    </div>
  );
}
function YesNoRadioDetail({ id, label, value, onChange, detail, setDetail, placeholder }) {
  return (
    <div className="space-y-2">
      <YesNoRadio id={id} label={label} value={value} onChange={onChange} />
      {value === true && (
        <Textarea required placeholder={`${placeholder} *`} value={detail} onChange={(e) => setDetail(e.target.value)} className="rounded-xl" data-testid={`${id}-detail`} />
      )}
    </div>
  );
}
function YesNoDetail({ id, label, checked, setChecked, detail, setDetail, placeholder }) {
  return (
    <div className="space-y-2">
      <CheckRow id={id} label={label} checked={checked} onChange={setChecked} />
      {checked && <Textarea placeholder={placeholder} value={detail} onChange={(e) => setDetail(e.target.value)} className="rounded-xl ml-7 w-[calc(100%-1.75rem)]" />}
    </div>
  );
}
